// The nightly books check on Books › Overview, in words (books.integrityStatus): when it last ran,
// what broke a rule and where to look, and ServiceTitan's own figures kept apart (never a problem).

import { dateText, timeText } from '@flowology/kernel';
import type { DocRef } from '../shared/doc-link.ts';
import type { BooksPart } from './props.ts';

export interface IntegrityRow {
  key: string;
  label: string;
  /** The document the label names, for its link (null for a stock count, an account, a technician's day). */
  doc?: DocRef | null;
}
export interface IntegrityCheck {
  id: string;
  title: string;
  unit: string;
  outcome: 'pass' | 'fail' | 'nothing-to-check' | 'error';
  examined: number;
  says: string | null;
  rows: IntegrityRow[];
  imported: IntegrityRow[];
  error: string | null;
}
export interface IntegrityStatus {
  ranAt: string;
  failing: number;
  imported: number;
  unfinished: number;
  checks: IntegrityCheck[];
}

/** Where a rule's documents are looked at in Books: the ledger's, the invoices' and payments', the hours'. */
const WHERE: [RegExp, BooksPart, string][] = [
  [/^ledger\./, 'ledger', 'Ledger & reports'],
  [/^(invoices|payments)\./, 'receivables', 'Receivables'],
  [/^time\./, 'payroll', 'Payroll'],
];

export interface IntegrityProblem {
  id: string;
  /** "1 invoice whose paid amount doesn't match its payments" */
  says: string;
  /** The documents, by number: "INV-1042, INV-1043 and 2 more". */
  names: string;
  /** The same, each with its link when it has one, and how many more there are. */
  shown: IntegrityRow[];
  more: number;
  go: { part: BooksPart; label: string } | null;
}

export interface IntegrityView {
  tone: 'ok' | 'warn' | 'none';
  head: string;
  problems: IntegrityProblem[];
  /** Rules that couldn't finish (a timeout, say): never read as a pass, never hidden. */
  unfinished: string[];
  /** Said under the problems when some also couldn't finish: "1 more books check couldn't finish: …". */
  alsoUnfinished: string | null;
  /** ServiceTitan's own figures, by rule: for the migration's reconciliation. */
  imported: { title: string; names: string; shown: IntegrityRow[]; more: number }[];
  importedCount: number;
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "INV-1042, INV-1043 and 2 more": the first few, so a long list stays one line. */
export function namesText(rows: readonly IntegrityRow[], shown = 4): string {
  const head = rows.slice(0, shown).map((r) => r.label);
  const more = rows.length - head.length;
  return more > 0 ? `${head.join(', ')} and ${more} more` : head.join(', ');
}

/** The first few rows (as namesText shows them) and how many more. */
export function firstFew(rows: readonly IntegrityRow[], shown = 4): { shown: IntegrityRow[]; more: number } {
  return { shown: rows.slice(0, shown), more: Math.max(0, rows.length - shown) };
}

/** "3:04 a.m." when it ran today in the company's zone, else "Oct 15, 3:04 a.m." (a missed night shows). */
export function ranText(iso: string, timeZone: string, now = new Date()): string {
  const time = timeText(iso, timeZone);
  const day = dateText(iso, timeZone, { year: false });
  return day === dateText(now.toISOString(), timeZone, { year: false }) ? time : `${day}, ${time}`;
}

export function integrityView(
  s: IntegrityStatus | null,
  timeZone: string,
  now = new Date(),
  /** The Books parts this person opens: a row links only to one of them (no Payroll for a manager). */
  opens: (part: BooksPart) => boolean = () => true,
): IntegrityView {
  const empty = { problems: [], unfinished: [], alsoUnfinished: null, imported: [], importedCount: 0 };
  if (!s)
    return {
      tone: 'none',
      head: 'The nightly books check hasn’t run yet. It runs each night from 3 a.m.',
      ...empty,
    };
  const when = ranText(s.ranAt, timeZone, now);
  const problems = s.checks
    .filter((c) => c.outcome === 'fail' && c.rows.length > 0)
    .map((c) => {
      const w = WHERE.find(([re]) => re.test(c.id));
      return {
        id: c.id,
        says: capital(c.says ?? `${plural(c.rows.length, 'record', 'records')} breaking “${c.title}”`),
        names: namesText(c.rows),
        ...firstFew(c.rows),
        go: w && opens(w[1]) ? { part: w[1], label: w[2] } : null,
      };
    });
  const unfinished = s.checks.filter((c) => c.outcome === 'error').map((c) => c.title);
  const imported = s.checks
    .filter((c) => c.imported.length > 0)
    .map((c) => ({ title: c.title, names: namesText(c.imported), ...firstFew(c.imported) }));
  const importedCount = s.checks.reduce((n, c) => n + c.imported.length, 0);
  const alsoUnfinished =
    problems.length && unfinished.length
      ? `${plural(unfinished.length, 'more books check', 'more books checks')} couldn’t finish: ${unfinished.join(', ')}. It runs again tonight.`
      : null;
  const rest = { problems, unfinished, alsoUnfinished, imported, importedCount };
  if (problems.length)
    return {
      tone: 'warn',
      head: `${plural(problems.length, 'books check', 'books checks')} found a problem (${when}). Nothing was changed; look at ${problems.length === 1 ? 'it' : 'them'} below.`,
      ...rest,
    };
  const passed = s.checks.some((c) => c.outcome === 'pass');
  if (unfinished.length)
    return {
      tone: 'warn',
      head: `${plural(unfinished.length, 'books check', 'books checks')} couldn’t finish (${when}): ${unfinished.join(', ')}. ${passed ? 'The rest passed. ' : ''}It runs again tonight.`,
      ...rest,
    };
  // "Nothing to check" isn't a pass: a company with no books yet says so.
  if (!passed) return { tone: 'none', head: `Nothing to check in the books yet (${when}).`, ...rest };
  return { tone: 'ok', head: `All books checks passed (${when}).`, ...rest };
}
